import { User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Minister } from '@/models/minister';

interface AuthorityCardProps {
  positionLabel: string;
  minister: Minister | null | undefined;
  /** Shows the Asignar / Cambiar button */
  canAssign: boolean;
  onAssign?: () => void;
  className?: string;
}

function getPhotoUrl(filename: string | null): string | null {
  if (!filename) return null;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/MinisterImages/ProfilePhotos/${filename}`;
}

export function AuthorityCard({ 
  positionLabel, 
  minister, 
  canAssign, 
  onAssign, 
  className 
}: AuthorityCardProps) {
  const photoUrl = minister?.profile_picture_filename ? getPhotoUrl(minister.profile_picture_filename) : null;
  
  return (
    <div className={cn(
      "rounded-lg border border-border bg-card p-4 shadow-sm",
      className
    )}>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{positionLabel}</h4>
        {canAssign && (
          <button
            onClick={onAssign}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            {minister ? 'Cambiar' : 'Asignar'}
          </button>
        )}
      </div>
      
      {minister ? (
        <div className="flex items-center gap-3">
          <div className="size-12 shrink-0 overflow-hidden rounded-full bg-muted">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={minister.full_name || ''}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center bg-primary/10">
                <User className="size-6 text-primary" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {minister.full_name}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {minister.adb_code}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="size-12 shrink-0 overflow-hidden rounded-full bg-muted">
            <div className="flex size-full items-center justify-center">
              <User className="size-6" />
            </div>
          </div>
          <p className="text-sm italic">Sin asignar</p>
        </div>
      )}
    </div>
  );
}
